/**
 * `BuyerDetailRetentionSection` — per-account buyer-detail retention
 * window picker (ADR-0065 §8). Lives under Settings → General; mounted
 * by `SettingsGeneral.tsx` as a separate Card section.
 *
 * Three radio options:
 *
 *   - `30_days`                  — minimum-retention compliance posture
 *   - `90_days` (default)        — typical chargeback/dispute window
 *   - `tie_to_retention_class`   — matches each record's retention_class
 *                                  (30d / 3y / 7y)
 *
 * Role gate: owner/admin only. The server returns 403 for reviewer +
 * viewer; the section hides the controls (read-only) when the session
 * role is not owner/admin. This mirrors the existing Settings role
 * pattern (e.g. `ConnectStripeSection` disables the toggle for
 * non-owner/admin roles).
 *
 * The change is NOT retroactive — the server computes `expires_at` at
 * INSERT time using the per-account setting at that moment; existing
 * rows keep their original horizon. The section surfaces this in
 * helper copy so an admin tightening 90d → 30d isn't surprised when
 * older rows survive the cutover.
 *
 * State:
 *   - Bootstrap: GET on mount → populates the radio.
 *   - Edit: PUT on radio change with the chosen value; optimistic
 *     update; error rollback with toast on failure.
 */

import { useCallback, useEffect, useState } from 'react';

import type { UserRole } from '@sill/types';

import { useOptionalHttp } from '../../api/http-context.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { useToast } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { Card } from '../../components/atoms/index.js';
import {
  getBuyerDetailRetention,
  putBuyerDetailRetention,
  type BuyerDetailRetentionSetting,
} from '../../api/account-buyer-detail-retention.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

export interface BuyerDetailRetentionSectionProps {
  /**
   * Optional role override for tests that don't wire `<AuthProvider />`.
   * Production reads via `useAuth()`.
   */
  role_override?: UserRole;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; setting: BuyerDetailRetentionSetting }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string };

interface RadioOption {
  readonly value: BuyerDetailRetentionSetting;
  readonly label: string;
  readonly hint: string;
}

const OPTIONS: readonly RadioOption[] = [
  {
    value: '30_days',
    label: '30 days',
    hint: 'Minimum-retention compliance posture.',
  },
  {
    value: '90_days',
    label: '90 days',
    hint: 'Recommended for typical chargeback and dispute review.',
  },
  {
    value: 'tie_to_retention_class',
    label: "Match each record's retention class",
    hint: '30 days for short-term records, 3 years for standard, 7 years for long-term.',
  },
];

export function BuyerDetailRetentionSection({
  role_override,
}: BuyerDetailRetentionSectionProps = {}): JSX.Element | null {
  const C = useC();
  const http = useOptionalHttp();
  const auth = useOptionalAuth();
  const toast = useToast();

  const role: UserRole | null =
    role_override !== undefined
      ? role_override
      : auth !== null && auth.state.kind === 'authenticated'
        ? auth.state.user.role
        : null;

  const can_read = role === 'owner' || role === 'admin';
  // Server enforces owner/admin on PUT too — viewer/reviewer don't see
  // controls.
  const can_write = can_read;

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  // Per-row pending — the radio being persisted; disables all radios
  // until the PUT resolves so a fast double-click doesn't race.
  const [pending, setPending] = useState<BuyerDetailRetentionSetting | null>(
    null,
  );

  useEffect(() => {
    if (!can_read) {
      setState({ kind: 'forbidden' });
      return;
    }
    if (http === null) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    void (async () => {
      const result = await getBuyerDetailRetention(http);
      if (cancelled) return;
      switch (result.kind) {
        case 'ok':
          setState({ kind: 'ready', setting: result.setting });
          break;
        case 'forbidden':
          setState({ kind: 'forbidden' });
          break;
        case 'error':
          setState({ kind: 'error', message: result.message });
          break;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [http, can_read]);

  const onChoose = useCallback(
    async (next: BuyerDetailRetentionSetting): Promise<void> => {
      if (!can_write) return;
      if (state.kind !== 'ready') return;
      if (state.setting === next) return;
      if (http === null) return;
      const previous = state.setting;
      setPending(next);
      // Optimistic update — flip the radio immediately so the UI is
      // responsive.
      setState({ kind: 'ready', setting: next });
      const result = await putBuyerDetailRetention(http, next);
      setPending(null);
      switch (result.kind) {
        case 'ok':
          setState({ kind: 'ready', setting: result.setting });
          toast({
            tone: 'lime',
            title: 'Retention setting saved.',
            body: 'Applies to newly captured transactions.',
          });
          break;
        case 'forbidden':
          // Role demoted mid-session. Roll back and surface.
          setState({ kind: 'ready', setting: previous });
          toast({
            tone: 'coral',
            title: 'Your role cannot change this setting.',
          });
          break;
        case 'bad_request':
          setState({ kind: 'ready', setting: previous });
          toast({
            tone: 'coral',
            title: 'That value is not a supported retention option.',
          });
          break;
        case 'rate_limited':
          setState({ kind: 'ready', setting: previous });
          toast({
            tone: 'amber',
            title: result.message,
          });
          break;
        case 'error':
          setState({ kind: 'ready', setting: previous });
          toast({
            tone: 'coral',
            title: 'Could not save retention setting.',
            body: result.message,
          });
          break;
      }
    },
    [can_write, http, state, toast],
  );

  // Non-owner/admin: hide the entire section. The route-guard pattern
  // for Settings sections is "render nothing for unprivileged roles"
  // rather than "render a read-only stub" — matches how Team, Audit,
  // Danger tabs behave (Phase-2 disabled = hidden tab) and avoids
  // surfacing controls the role can't use.
  if (!can_read) return null;

  return (
    <Card className="p-5 sm:p-6 mb-5">
      <div className="mb-5 pb-4" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            marginBottom: 6,
          }}
        >
          BUYER DETAIL RETENTION
        </div>
        <div
          style={{
            fontFamily: serifFont,
            fontSize: 22,
            color: C.text,
            letterSpacing: -0.5,
            marginBottom: 4,
          }}
        >
          How long Sill keeps decryptable buyer detail
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
          Buyer email, name, phone, and shipping address are AES-256-GCM
          encrypted at rest and decryptable by your owner, admin, and
          reviewer roles. The setting applies to newly captured
          transactions only — existing records keep their original
          horizon.
        </div>
      </div>

      {state.kind === 'loading' ? (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            letterSpacing: 1,
          }}
        >
          Loading…
        </div>
      ) : null}

      {state.kind === 'error' ? (
        <div
          className="mt-2"
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.coralFg,
            lineHeight: 1.5,
          }}
        >
          Couldn't load retention setting — {state.message}
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        <fieldset
          aria-label="Buyer detail retention"
          className="space-y-2"
          style={{ border: 'none', padding: 0, margin: 0 }}
        >
          {OPTIONS.map((opt) => {
            const checked = state.setting === opt.value;
            const is_pending = pending === opt.value;
            return (
              <label
                key={opt.value}
                className="flex items-start gap-3 p-3 cursor-pointer"
                style={{
                  background: checked ? C.surface2 : 'transparent',
                  border: `1px solid ${checked ? C.lime : C.border}`,
                  borderRadius: 2,
                  opacity: pending !== null && !is_pending ? 0.6 : 1,
                  cursor: pending !== null || !can_write ? 'not-allowed' : 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="buyer_detail_retention"
                  value={opt.value}
                  checked={checked}
                  disabled={!can_write || pending !== null}
                  onChange={() => void onChoose(opt.value)}
                  className="mt-0.5"
                  style={{ accentColor: C.lime }}
                />
                <div className="flex-1">
                  <div
                    style={{
                      fontFamily: sansFont,
                      fontSize: 13,
                      color: C.text,
                      fontWeight: 500,
                    }}
                  >
                    {opt.label}
                  </div>
                  <div
                    style={{
                      fontFamily: sansFont,
                      fontSize: 12,
                      color: C.muted,
                      marginTop: 2,
                      lineHeight: 1.5,
                    }}
                  >
                    {opt.hint}
                  </div>
                </div>
              </label>
            );
          })}
        </fieldset>
      ) : null}
    </Card>
  );
}
